import honeybadger from "@honeybadger-io/js";
import { getServerInfo } from "./server";

let instance: honeybadger;

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
const delayedContext: any[] = [];

interface HoneybadgerFrame {
    file?: string;
    function?: string;
}

interface HoneybadgerNotice {
    name?: string;
    message?: string;
    stack?: string;
    backtrace?: HoneybadgerFrame[];
}

export const isExternalError = (notice: HoneybadgerNotice): boolean => {
    // Check stack trace for chrome-extension URLs (more reliable than backtrace parsing)
    if (notice?.stack && /chrome-extension:\/\//.test(notice.stack)) {
        return true;
    }

    if (!notice?.backtrace?.length) {
        return false;
    }

    const allFramesAreUnknown = notice.backtrace.every(
        (frame: HoneybadgerFrame) =>
            frame?.file?.includes("chrome-extension://") ||
            frame?.file?.includes("<unknown>") ||
            frame?.file?.match(/<anonymous.*>/),
    );

    return allFramesAreUnknown;
};

export const shouldFilterDynamicImportError = (notice: HoneybadgerNotice): boolean => {
    if (!notice?.message) {
        return false;
    }

    return notice.message.includes("Failed to fetch dynamically imported module");
};

export const configureErrorTracking = async () => {
    const serverInfo = await getServerInfo();
    if (!instance) {
        honeybadger.beforeNotify((notice): boolean | void => {
            if (
                notice?.name === "ClientGraphqlNetworkError" ||
                notice?.name === "ClientHttpNetworkError" ||
                notice?.name === "UnauthenticatedError"
            ) {
                return false;
            }

            if (notice?.message?.includes("ResizeObserver")) {
                return false;
            }

            if (shouldFilterDynamicImportError(notice)) {
                return false;
            }

            if (
                notice?.message?.includes("Unexpected keyword 'import'") &&
                (notice?.backtrace?.some(frame => frame?.file?.includes("<unknown>")) ||
                    notice?.stack?.includes("blob:"))
            ) {
                return false;
            }

            if (
                notice?.message?.includes("opacityVertexArray.length") ||
                notice?.message?.includes("layoutVertexArray.length")
            ) {
                return false;
            }

            if (
                notice?.name === "ChunkLoadError" &&
                notice?.message?.includes("beacon")
            ) {
                return false;
            }

            // Filter out CefSharp bot errors (Outlook SafeLink scanners, etc.)
            if (
                notice?.message?.match(
                    /Object Not Found Matching Id:\d+, MethodName:update/,
                )
            ) {
                return false;
            }

            // Filter MetaMask browser extension errors
            if (notice?.message?.includes("Failed to connect to MetaMask")) {
                return false;
            }

            if (
                notice?.message?.includes("Could not compile fragment shader") ||
                notice?.message?.includes("Could not compile vertex shader")
            ) {
                return false;
            }

            if (isExternalError(notice)) {
                return false;
            }
        });
        instance = honeybadger.configure({
            apiKey: "6502fcd2",
            environment: serverInfo.environment,
            revision: serverInfo.build,
        });
    }

    delayedContext.forEach(x => x(instance));
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
(window as any).customReport = () => {
    try {
        throw "this is a test!";
    } catch (e) {
        honeybadger.notify(e instanceof Error ? e : new Error(String(e)));
    }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
(window as any).customReportOnInstance = () => {
    try {
        throw "this is a test!";
    } catch (e) {
        instance.notify(e instanceof Error ? e : new Error(String(e)));
    }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
export const setErrorTrackingContext = (context: any) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
    const callback = (i: any) => i.setContext(context);
    if (instance?.setContext) {
        callback(instance);
    } else {
        delayedContext.push(callback);
    }
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any -- eslintintroduction
export const reportError = (e: Error, context: any) => {
    if (!instance) return;
    instance.notify(e, undefined, { context });
};
